import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import type { SubmissionListResponseOutputItemsItem } from '@/api/generated';
import AnnotationBadges from '@/components/blocks/shared/annotation-badges';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

// Statuses with a human-readable label; anything else (pipeline-written
// values we don't recognize) renders as the raw string.
const STATUS_LABEL_KEYS = {
  processed: 'submissions.status.processed',
  annotation_in_progress: 'submissions.status.annotation_in_progress',
  annotation_completed: 'submissions.status.annotation_completed',
  kpi_data_collected: 'submissions.status.kpi_data_collected',
} as const;

const getStatusLabelKey = (
  status: string
): (typeof STATUS_LABEL_KEYS)[keyof typeof STATUS_LABEL_KEYS] | undefined =>
  STATUS_LABEL_KEYS[status as keyof typeof STATUS_LABEL_KEYS];

const STATUS_BADGE_VARIANTS: Partial<Record<string, BadgeVariant>> = {
  annotation_completed: 'success',
  kpi_data_collected: 'brand',
};

const getStatusBadgeVariant = (status: string): BadgeVariant =>
  STATUS_BADGE_VARIANTS[status] ?? 'outline';

interface SubmissionRowProps {
  submission: SubmissionListResponseOutputItemsItem;
  customerId: string;
  className?: string;
}

export const SubmissionsTableRow: FC<SubmissionRowProps> = ({
  submission,
  customerId,
  className,
}: SubmissionRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = submission.status;
  const statusLabelKey = status ? getStatusLabelKey(status) : undefined;

  return (
    <TableRow
      className={cn(className)}
      onClick={async () =>
        navigate({
          to: '/app/shelves/$customerId/$submissionId',
          params: {
            customerId,
            submissionId: String(submission.id),
          },
        })
      }>
      <TableCell>
        <Trans
          i18nKey='submissions.itemTitle'
          values={{ id: submission.id }}
        />
      </TableCell>
      <TableCell>
        {submission.departmentCode ? (
          <Badge
            variant='secondary'
            className='font-mono font-medium'>
            {submission.departmentCode}
          </Badge>
        ) : (
          <span className='text-muted-foreground'>—</span>
        )}
      </TableCell>
      <TableCell>
        <AnnotationBadges counts={submission.counts} />
      </TableCell>
      <TableCell>
        {status && (
          <Badge variant={getStatusBadgeVariant(status)}>
            {statusLabelKey ? t(statusLabelKey) : status}
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
};
